const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[role="button"]:not([disabled])',
    '[role="menuitem"]',
    '[tabindex]:not([tabindex="-1"])'
].join(', ');

const ACTIVE_INDEX_ATTR = 'data-active-index';
const EVENT_LISTENER_ATTR = 'data-menu-navigation-enabled';

function handleFocusOut(event: FocusEvent, closeHandler: () => void, dropdown: HTMLElement) {
    if (!dropdown.contains(event.relatedTarget as Node)) {
        closeHandler?.();
    }
}

const toggleMenu = (menu: HTMLElement, forceState?: boolean) => {
    const isOpen = menu.classList.contains("active");
    const newState = forceState || !isOpen;

    menu.classList.toggle("active", newState);

    if (newState) {
        const firstItem = menu.querySelector<HTMLElement>('[role="menuitem"], a, button, input, [tabindex]:not([tabindex="-1"])');
        firstItem?.focus();
    }
};

function handleItemFocus(event: Event, menu: HTMLElement) {
    const target = event.target as HTMLElement;
    const focusableItems = Array.from(menu.querySelectorAll<HTMLElement>(focusableSelectors));
    const index = focusableItems.indexOf(target);
    if (index >= 0) {
        menu.setAttribute(ACTIVE_INDEX_ATTR, index.toString());
    }
}

function handleKeyDown(
    event: KeyboardEvent,
    trigger?: HTMLElement,
    closeHandler?: () => void,
    menu?: HTMLElement,
) {
    if (!menu) return;
    const key = event.key;

    const focusableItems = Array.from(menu.querySelectorAll<HTMLElement>(focusableSelectors))
        .filter(item => !item.hidden && item.offsetParent !== null);

    if (focusableItems.length === 0) return;

    const currentElement = document.activeElement as HTMLElement;
    let currentIndex = focusableItems.indexOf(currentElement);
    if (currentIndex < 0) currentIndex = 0;

    let nextIndex = currentIndex;

    switch (key) {
        case "Escape":
            closeHandler?.();
            trigger?.focus();
            event.preventDefault();
            return;
        case "Tab":
            closeHandler?.();
            return;
        case "ArrowDown":
            nextIndex = (currentIndex + 1) % focusableItems.length;
            break;
        case "ArrowUp":
            nextIndex = (currentIndex - 1 + focusableItems.length) % focusableItems.length;
            break;
        case "Home":
            nextIndex = 0;
            break;
        case "End":
            nextIndex = focusableItems.length - 1;
            break;
        case "Enter":
        case " ":
            event.preventDefault();
            const currentItem = focusableItems[currentIndex];
            if (currentItem) {
                currentItem.click(); // simulate click
            }
            return;
        default:
            return;
    }

    const nextItem = focusableItems[nextIndex];
    if (nextItem) {
        const allIndex = focusableItems.indexOf(nextItem);
        menu.setAttribute(ACTIVE_INDEX_ATTR, allIndex.toString());
        nextItem.focus();
    }

    event.preventDefault();
    event.stopImmediatePropagation();
}

export const enableMenuNavigation = (
    dropdown: HTMLElement,
    trigger?: HTMLElement,
    closeHandler?: () => void,
    onItemSelect?: (item: HTMLElement) => void
) => {
    const menu = dropdown.querySelector('ul');
    if (menu.hasAttribute(EVENT_LISTENER_ATTR)) return;
    menu.setAttribute(EVENT_LISTENER_ATTR, "true");

    if (!menu.hasAttribute(ACTIVE_INDEX_ATTR)) {
        menu.setAttribute(ACTIVE_INDEX_ATTR, "0");
    }

    const menuItems = menu.querySelectorAll<HTMLElement>(focusableSelectors);
    menuItems.forEach((item) => {
        item.addEventListener("focus", (event) => handleItemFocus(event, menu));
        if (onItemSelect) {
            item.addEventListener("click", () => onItemSelect(item));
        }
    });

    const focusHandler = (event: FocusEvent) => handleFocusOut(event, closeHandler, dropdown);
    const keyHandler = (event: KeyboardEvent) => handleKeyDown(event, trigger, closeHandler, menu);

    menu.addEventListener("focusout", focusHandler);
    menu.addEventListener("keydown", keyHandler);

    return () => {
        menu.removeAttribute(EVENT_LISTENER_ATTR);
        menu.removeEventListener("focusout", focusHandler);
        menu.removeEventListener("keydown", keyHandler);
        menuItems.forEach((item) => {
            item.removeEventListener("focus", (event) => handleItemFocus(event, menu));
            if (onItemSelect) {
                item.removeEventListener("click", () => onItemSelect(item));
            }
        });
    };
};


export { focusableSelectors, toggleMenu };
